import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Buildings, User, ShieldCheck, Prohibit, ArrowCounterClockwise, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StripeCard } from "@/components/admin/StripeCard";

const Pill = ({ tone, children, testid }) => {
  const c = { red: "border-red-500/30 bg-red-500/10 text-red-300", green: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    zinc: "border-white/10 bg-white/5 text-zinc-400", amber: "border-amber-500/30 bg-amber-500/10 text-amber-300" }[tone];
  return <span data-testid={testid} className={`text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase ${c}`}>{children}</span>;
};

// action: { kind: "user"|"org", op: "suspend"|"reactivate", id, label, confirmText }
const ActionDialog = ({ action, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); setConfirm(""); }, [action]);
  if (!action) return null;
  const suspend = action.op === "suspend";
  const ok = reason.trim().length >= 3 && (!suspend || confirm.trim().toLowerCase() === action.confirmText.toLowerCase());
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/platform/${action.kind === "user" ? "users" : "orgs"}/${action.id}/${action.op}`, { reason, confirm });
      toast.success(`${action.label} ${suspend ? "suspended" : "reactivated"}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10" data-testid="platform-action-dialog" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{suspend ? "Suspend" : "Reactivate"} {action.kind === "user" ? "account" : "organization"}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {suspend
              ? `${action.label} will lose access immediately, including already signed-in sessions and live remote sessions. No data is deleted, and you can reactivate at any time.`
              : `${action.label} will regain access with all data intact.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-400">Reason (recorded in the audit log)</Label>
            <Textarea data-testid="platform-action-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1 bg-black border-zinc-800" />
          </div>
          {suspend && (
            <div>
              <Label className="text-xs text-zinc-400">Type <span className="font-mono text-zinc-200">{action.confirmText}</span> to confirm</Label>
              <Input data-testid="platform-action-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 bg-black border-zinc-800" />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
          <Button data-testid="platform-action-submit" disabled={!ok || busy} onClick={run} className={suspend ? "bg-red-600 hover:bg-red-500" : "bg-emerald-600 hover:bg-emerald-500"}>
            {busy ? "Working…" : suspend ? "Suspend" : "Reactivate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MemberRow = ({ m, onAction }) => (
  <div data-testid={`platform-user-${m.user_id}`} className="flex items-center gap-3 py-2 px-3">
    <User size={14} className="text-zinc-500 shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="text-sm truncate">{m.name || m.email} <span className="text-xs text-zinc-500">{m.email}</span></div>
      <div className="flex gap-1.5 mt-0.5 flex-wrap">
        <Pill tone="zinc">{m.role}</Pill>
        {m.is_platform_admin && <Pill tone="amber">platform admin</Pill>}
        {m.suspended ? <Pill tone="red" testid={`user-status-${m.user_id}`}>suspended</Pill> : <Pill tone="green" testid={`user-status-${m.user_id}`}>active</Pill>}
      </div>
    </div>
    {!m.is_platform_admin && (m.suspended
      ? <Button size="sm" variant="ghost" data-testid={`reactivate-user-${m.user_id}`} onClick={() => onAction({ kind: "user", op: "reactivate", id: m.user_id, label: m.email })} className="h-7 text-xs text-emerald-400"><ArrowCounterClockwise size={13} className="mr-1" />Reactivate</Button>
      : <Button size="sm" variant="ghost" data-testid={`suspend-user-${m.user_id}`} onClick={() => onAction({ kind: "user", op: "suspend", id: m.user_id, label: m.email, confirmText: m.email })} className="h-7 text-xs text-red-400"><Prohibit size={13} className="mr-1" />Suspend</Button>)}
  </div>
);

const OrgCard = ({ o, onAction }) => {
  const [open, setOpen] = useState(false);
  const hasAdmin = o.members.some((m) => m.is_platform_admin);
  return (
    <div data-testid={`platform-org-${o.org_id}`} className="rounded-xl border border-white/10 bg-zinc-900/60">
      <div className="flex items-center gap-3 p-3 flex-wrap">
        <button onClick={() => setOpen(!open)} data-testid={`toggle-org-${o.org_id}`} className="flex items-center gap-2 min-w-[12rem] flex-1 text-left">
          {open ? <CaretDown size={14} className="shrink-0" /> : <CaretRight size={14} className="shrink-0" />}
          <Buildings size={16} className="text-zinc-400 shrink-0" />
          <span className="min-w-0">
            <span className="block font-medium truncate">{o.name}</span>
            <span className="block text-xs text-zinc-500">{o.members.length} users · {o.machine_count} machines</span>
          </span>
        </button>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Pill tone="zinc" testid={`org-plan-${o.org_id}`}>{o.plan === "internal" ? `unlimited (internal) · base ${o.base_plan}` : o.plan}</Pill>
          {o.subscription_status && <Pill tone={o.subscription_status === "active" ? "green" : "zinc"}>sub: {o.subscription_status}</Pill>}
          {o.has_stripe_subscription && <Pill tone="amber">stripe</Pill>}
          {o.plan_granted_by_admin && <Pill tone="zinc">granted</Pill>}
          {o.suspended ? <Pill tone="red" testid={`org-status-${o.org_id}`}>suspended</Pill> : <Pill tone="green" testid={`org-status-${o.org_id}`}>active</Pill>}
          {!hasAdmin && (o.suspended
            ? <Button size="sm" variant="ghost" data-testid={`reactivate-org-${o.org_id}`} onClick={() => onAction({ kind: "org", op: "reactivate", id: o.org_id, label: o.name })} className="h-7 text-xs text-emerald-400">Reactivate</Button>
            : <Button size="sm" variant="ghost" data-testid={`suspend-org-${o.org_id}`} onClick={() => onAction({ kind: "org", op: "suspend", id: o.org_id, label: o.name, confirmText: o.name })} className="h-7 text-xs text-red-400">Suspend</Button>)}
        </div>
      </div>
      {o.suspended && o.suspended_reason && <div className="px-3 pb-2 text-xs text-red-300/80">Reason: {o.suspended_reason}</div>}
      {open && <div className="border-t border-white/5 divide-y divide-white/5">{o.members.map((m) => <MemberRow key={m.user_id} m={m} onAction={onAction} />)}</div>}
    </div>
  );
};

export function AccountsTab() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [action, setAction] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/platform/accounts").then((r) => setData(r.data)).catch(() => toast.error("Could not load accounts"));
    api.get("/admin/platform/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  const togglePrivate = async (v) => {
    try { await api.post("/admin/settings/private-mode", { enabled: v }); toast.success(v ? "Private mode on" : "Private mode off — public signup & checkout re-enabled"); load(); }
    catch { toast.error("Could not change private mode"); }
  };
  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="accounts-tab">
      <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4 flex items-center gap-3">
        <ShieldCheck size={20} className="text-blue-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Private platform mode</div>
          <div className="text-xs text-zinc-500">When on, public signup, organization creation and new subscriptions are disabled. Existing accounts and subscriptions are unaffected.</div>
        </div>
        <Switch data-testid="private-mode-switch" checked={data.private_mode} onCheckedChange={togglePrivate} />
      </div>
      <StripeCard />
      <div className="space-y-2">
        {data.organizations.map((o) => <OrgCard key={o.org_id} o={o} onAction={setAction} />)}
      </div>
      <div>
        <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-2">Platform audit log</h3>
        <div className="rounded-xl border border-white/10 divide-y divide-white/5" data-testid="platform-audit">
          {audit.length === 0 && <div className="p-3 text-sm text-zinc-500">No platform actions yet.</div>}
          {audit.map((a) => (
            <div key={a.audit_id} className="p-3 text-xs">
              <span className="font-mono text-zinc-300">{a.action.replace(/_/g, " ")}</span> · {a.target_label} · <span className="text-zinc-500">{a.reason}</span>
              <div className="text-zinc-600 mt-0.5">{new Date(a.created_at).toLocaleString()} by {a.actor_email}</div>
            </div>
          ))}
        </div>
      </div>
      <ActionDialog action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />
    </div>
  );
}
